import { useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  TableBodySkeleton,
} from '../../../common/components';
import { useInventoryArticle } from '../../../hooks/useInventoryArticles';
import { useArticleStocksByArticle } from '../../../hooks/useArticleStocks';
import { useStockMovements } from '../../../hooks/useStockMovements';
import { useUnits } from '../../../hooks/useUnits';
import { ALL_WAREHOUSES_FILTER } from '../constants/inventoryArticlesConstants';
import {
  getArticleLabel,
  getUnitDescription,
  getWarehouseLabel,
} from '../utils/inventoryArticlesUtils';
import { useWarehousesByUuids } from '../hooks/useWarehousesByUuids';
import ArticleStockByWarehouseTable from './ArticleStockByWarehouseTable';
import ArticleStockMovementsTable from './ArticleStockMovementsTable';
import AdjustArticleStockDialog from './AdjustArticleStockDialog';

const InventoryArticleDetailDialog = ({ open, onClose, articleId }) => {
  const [selectedStockRow, setSelectedStockRow] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState(ALL_WAREHOUSES_FILTER);

  const { data: units = [] } = useUnits();

  const { data: article, isLoading: articleLoading } = useInventoryArticle(articleId, {
    enabled: open && Boolean(articleId),
  });

  const {
    data: stocks = [],
    isLoading: stocksLoading,
    refetch: refetchStocks,
  } = useArticleStocksByArticle(articleId, {
    enabled: open && Boolean(articleId),
  });

  const movementFilters = useMemo(() => {
    const filters = { uuid_inventory_article: articleId };

    if (warehouseFilter !== ALL_WAREHOUSES_FILTER) {
      filters.uuid_warehouse = warehouseFilter;
    }

    return filters;
  }, [articleId, warehouseFilter]);

  const { data: movements = [], isLoading: movementsLoading } = useStockMovements(
    movementFilters,
    { enabled: open && Boolean(articleId) }
  );

  const warehouseUuids = useMemo(
    () => [
      ...new Set([
        ...stocks.map((stock) => stock.uuid_warehouse),
        ...movements.map((movement) => movement.uuid_warehouse),
      ]),
    ],
    [stocks, movements]
  );

  const { warehousesByUuid, isLoading: warehousesLoading } = useWarehousesByUuids(
    warehouseUuids,
    open && Boolean(articleId)
  );

  const warehouseOptions = useMemo(
    () =>
      warehouseUuids.map((uuid) => ({
        value: uuid,
        label: getWarehouseLabel(warehousesByUuid, uuid),
      })),
    [warehouseUuids, warehousesByUuid]
  );

  const sortedMovements = useMemo(
    () =>
      [...movements].sort(
        (left, right) => new Date(right.created_at) - new Date(left.created_at)
      ),
    [movements]
  );

  const handleOpenAdjustDialog = (stockRow) => {
    setSelectedStockRow(stockRow);
    setAdjustDialogOpen(true);
  };

  const handleCloseAdjustDialog = () => {
    setAdjustDialogOpen(false);
    setSelectedStockRow(null);
  };

  const handleAdjustSuccess = async () => {
    await refetchStocks();
  };

  const handleClose = () => {
    setWarehouseFilter(ALL_WAREHOUSES_FILTER);
    setSelectedStockRow(null);
    setAdjustDialogOpen(false);
    onClose();
  };

  const isLoading = articleLoading;
  const stockSectionLoading = stocksLoading || warehousesLoading;

  if (!open) return null;

  if (isLoading || !article) {
    return (
      <DialogContainer open={open} onClose={handleClose} size="large" minHeight="60vh">
        <DialogTitle>Detalle del artículo</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={handleClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <>
      <DialogContainer open={open} onClose={handleClose} size="large" minHeight="70vh">
        <DialogTitle>{getArticleLabel(article)}</DialogTitle>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Código: ${article.code}`} variant="outlined" />
              <Chip
                label={`Unidad: ${getUnitDescription(units, article.uuid_unit)}`}
                variant="outlined"
              />
              <Chip
                label={article.is_active ? 'Activo' : 'Inactivo'}
                color={article.is_active ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>

            {article.description && (
              <Typography variant="body2" color="text.secondary">
                {article.description}
              </Typography>
            )}

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Stock por depósito
              </Typography>
              <ArticleStockByWarehouseTable
                stocks={stocks}
                warehousesByUuid={warehousesByUuid}
                isLoading={stockSectionLoading}
                canModifyStock
                onAdjustStock={handleOpenAdjustDialog}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Historial de movimientos
              </Typography>
              <ArticleStockMovementsTable
                movements={sortedMovements}
                warehousesByUuid={warehousesByUuid}
                warehouseFilter={warehouseFilter}
                warehouseOptions={warehouseOptions}
                onWarehouseFilterChange={setWarehouseFilter}
                isLoading={movementsLoading || warehousesLoading}
              />
            </Box>
          </Box>
        </DialogBody>

        <DialogActions
          handleClose={handleClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>

      <AdjustArticleStockDialog
        open={adjustDialogOpen}
        onClose={handleCloseAdjustDialog}
        article={article}
        stockRow={selectedStockRow}
        warehousesByUuid={warehousesByUuid}
        onSuccess={handleAdjustSuccess}
      />
    </>
  );
};

export default InventoryArticleDetailDialog;
